import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { mediaUrl } from '../../helpers/mediaUrl';

// ─── DESIGN TOKENS (iNOVAA dark) ──────────────────────────────────
const T = {
  bg: 'var(--nd-bg)',
  surface: 'var(--nd-surface)',
  border: 'var(--nd-w06)',
  text: 'var(--nd-text)',
  textMuted: 'var(--nd-tx35)',
};

const API = process.env.REACT_APP_API_BASE_URL;

const GLOBAL = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
  .nv-fsr * { box-sizing: border-box; }
  .nv-fsr ::-webkit-scrollbar { width: 6px; height: 6px; }
  .nv-fsr ::-webkit-scrollbar-thumb { background: var(--nd-w08); border-radius: 4px; }
  .nv-fsr input, .nv-fsr select { background: var(--nd-w05); border: 1px solid var(--nd-w10); border-radius: 8px; color: var(--nd-text); font-family: 'DM Sans', sans-serif; font-size: 13px; padding: 8px 12px; outline: none; }
  .nv-fsr input:focus, .nv-fsr select:focus { border-color: rgba(96,165,250,0.5); }
  .nv-fsr select option { background: var(--nd-surface-2); color: var(--nd-text); }
  .nv-fsr input[type="date"] { color-scheme: dark; }
  .nv-fsr-card { transition: transform 0.12s, border-color 0.12s; }
  .nv-fsr-card:hover { transform: translateY(-2px); border-color: rgba(96,165,250,0.4) !important; }
`;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtDate = (s) => {
  if (!s) return '—';
  const [y, m, d] = String(s).split('-');
  if (!y || !m || !d) return s;
  return `${MONTHS[Number(m) - 1]} ${Number(d)}, ${y}`;
};
// Was `/uploads/job-photos/${basename}`, which discarded the real folder and
// broke every photo stored outside job-photos. Shared helper keeps the folder.
const imgUrl = (p) => mediaUrl(p) || null;

const FSRPhotos = () => {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [clientId, setClientId] = useState('');
  const [teamId, setTeamId] = useState('');
  const [clients, setClients] = useState([]);
  const [teams, setTeams] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  // Filter option lists
  useEffect(() => {
    axios.get(`${API}/commercial-clients`)
      .then(r => setClients(r.data?.data?.clients || []))
      .catch(() => setClients([]));
    axios.get(`${API}/schedule/teams?active_only=true`)
      .then(r => setTeams(r.data?.teams || []))
      .catch(() => setTeams([]));
  }, []);

  const fetchPhotos = async () => {
    try {
      setLoading(true); setError(null);
      const qs = new URLSearchParams();
      if (from) qs.append('from', from);
      if (to) qs.append('to', to);
      if (clientId) qs.append('client_id', clientId);
      if (teamId) qs.append('team_id', teamId);
      const q = qs.toString() ? `?${qs.toString()}` : '';
      const res = await axios.get(`${API}/fo-schedules/fsr-photos${q}`);
      setPhotos(res.data?.photos || []);
    } catch (err) {
      setError('Failed to load FSR photos');
      setPhotos([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchPhotos(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [from, to, clientId, teamId]);

  const card = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden' };

  return (
    <div className="nv-fsr" style={{ background: T.bg, fontFamily: "'DM Sans', sans-serif", color: T.text, padding: '14px 18px', minHeight: '100vh' }}>
      <style>{GLOBAL}</style>

      {/* Header */}
      <div style={{ ...card, marginBottom: 14 }}>
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${T.border}` }}>
          <div style={{ fontSize: 9, color: T.textMuted, letterSpacing: '0.18em', marginBottom: 5 }}>iNOVAA · C&I MANAGEMENT</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--nd-text-strong)', letterSpacing: '-0.03em', lineHeight: 1 }}>FSR Photos</div>
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 5 }}>Field Service Report photos by date, client & team</div>
        </div>
        <div style={{ height: 2, background: 'linear-gradient(90deg,#3B82F6,#22D3A5,transparent)' }} />

        {/* Filters */}
        <div style={{ padding: '12px 22px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
            <span style={{ color: T.textMuted, fontSize: 12 }}>→</span>
            <input type="date" value={to} onChange={e => setTo(e.target.value)} />
          </div>
          <select value={clientId} onChange={e => setClientId(e.target.value)} style={{ minWidth: 180 }}>
            <option value="">All clients</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.client_name}</option>)}
          </select>
          <select value={teamId} onChange={e => setTeamId(e.target.value)} style={{ minWidth: 160 }}>
            <option value="">All teams</option>
            {teams.map(t => <option key={t.id} value={t.id}>{t.team_lead_name || `Team ${t.id}`}</option>)}
          </select>
          <span style={{ marginLeft: 'auto', fontSize: 11, color: T.textMuted, fontFamily: "'DM Mono', monospace" }}>{photos.length} photo{photos.length !== 1 ? 's' : ''}</span>
          {(from || to || clientId || teamId) && (
            <button onClick={() => { setFrom(''); setTo(''); setClientId(''); setTeamId(''); }}
              style={{ padding: '8px 14px', background: 'var(--nd-w05)', border: `1px solid ${T.border}`, borderRadius: 8, color: T.textMuted, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
              Clear filters
            </button>
          )}
        </div>
      </div>

      {error && <div style={{ ...card, padding: '14px 20px', marginBottom: 14, color: '#F87171', fontSize: 13 }}>⚠ {error}</div>}

      {/* Grid */}
      <div style={{ ...card, padding: '18px' }}>
        {loading ? (
          <div style={{ padding: '50px 0', textAlign: 'center', color: T.textMuted, fontSize: 13 }}>Loading…</div>
        ) : photos.length === 0 ? (
          <div style={{ padding: '50px 0', textAlign: 'center', color: T.textMuted }}>
            <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.4 }}>🖼️</div>
            <div style={{ fontSize: 13 }}>No FSR photos for these filters</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
            {photos.map((p) => {
              const url = imgUrl(p.fsr_photo);
              return (
                <div key={p.fsr_id} className="nv-fsr-card" style={{ border: `1px solid ${T.border}`, borderRadius: 12, overflow: 'hidden', background: 'var(--nd-w02)' }}>
                  <img
                    src={url}
                    alt="FSR"
                    onClick={() => setLightbox(url)}
                    style={{ width: '100%', height: 160, objectFit: 'cover', cursor: 'pointer', display: 'block', borderBottom: `1px solid ${T.border}` }}
                  />
                  <div style={{ padding: '10px 12px' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 3 }}>{p.client_name || '—'}</div>
                    <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 6 }}>{p.site_name || '—'}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 10, color: T.textMuted, fontFamily: "'DM Mono', monospace" }}>
                      <span>{fmtDate(p.scheduled_date)}</span>
                      {p.shift && <span style={{ textTransform: 'capitalize' }}>{p.shift}</span>}
                    </div>
                    {p.team_lead_name && (
                      <div style={{ fontSize: 10, color: '#60A5FA', marginTop: 5 }}>👥 {p.team_lead_name}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div onClick={() => setLightbox(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: 24 }}>
          <img src={lightbox} alt="FSR" style={{ maxWidth: '90vw', maxHeight: '88vh', borderRadius: 12, border: '1px solid var(--nd-w15)' }} />
        </div>
      )}
    </div>
  );
};

export default FSRPhotos;
